import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";

const CategoriesPage = () => {
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editCategoryName, setEditCategoryName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [originalCategoryName, setOriginalCategoryName] = useState("");
  const [categoryType, setCategoryType] = useState("main");
  const [parentCategory, setParentCategory] = useState(null);
  const [editCategoryType, setEditCategoryType] = useState("");
  const [originalCategoryType, setOriginalCategoryType] = useState("");
  const [editParentCategory, setEditParentCategory] = useState("");
  const [originalParentCategory, setOriginalParentCategory] = useState("");

  const fetchCategories = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "categories",
        data: {},
      });

      setTimeout(() => {
        setCategories(res?.success ? res.data : []);
        setIsSubmitting(false);
      }, 500);
    } catch (err) {
      setIsSubmitting(false);
      setErrorWithTimeout("Failed to fetch categories");
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleAddCategoryClick = () => {
    setShowAddCategoryModal(true);
  };

  const handleCloseModal = () => {
    setShowAddCategoryModal(false);
    setShowDeleteModal(false);
    resetFormState();
  };

  const resetFormState = () => {
    setCategoryName("");
    setErrorWithTimeout("");
    setCategoryType("main");
    setParentCategory(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setErrorWithTimeout("");
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "create-category",
        data: {
          category_name: categoryName.trim(),
          category_type: categoryType,
          parent_id: categoryType === "sub" ? parentCategory : null,
        },
      });

      if (!res?.success) {
        if (res?.error === "Category already exists") {
          setErrorWithTimeout("Category already exists");
        } else {
          setErrorWithTimeout(res?.error || "Unexpected error occurred");
        }
        setIsSubmitting(false);
        return;
      }

      setTimeout(() => {
        setIsSubmitting(false);
        handleCloseModal();
        fetchCategories();
        showSuccess("Category added successfully");
      }, 500);
    } catch (err) {
      setIsSubmitting(false);

      if (err.message === "UNAUTHORIZED" || err.message === "SESSION_EXPIRED") {
        setErrorWithTimeout("Session expired. Please login again.");
      } else if (err.message === "REQUEST_TIMEOUT") {
        setErrorWithTimeout("Request timed out. Try again.");
      } else if (err.message === "NETWORK_ERROR") {
        setErrorWithTimeout("Network error. Check your connection.");
      } else {
        setErrorWithTimeout("Unexpected error occurred.");
      }
    }
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    resetEditFormState();
  };

  const resetEditFormState = () => {
    setEditCategoryId("");
    setEditCategoryName("");
    setEditCategoryType("");
    setEditParentCategory("");
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();

    setErrorWithTimeout("");
    setIsSubmitting(true);

    const isUnchanged =
      editCategoryName === originalCategoryName &&
      editCategoryType === originalCategoryType &&
      editParentCategory === originalParentCategory;

    if (isUnchanged) {
      setIsSubmitting(false);
      handleEditModalClose();
      return;
    }

    try {
      const res = await adminApiRequest({
        script: "update-category",
        data: {
          id: editCategoryId,
          category_name: editCategoryName.trim(),
          category_type: editCategoryType,
          parent_id: editCategoryType === "sub" ? editParentCategory : null,
        },
      });

      if (!res?.success) {
        if (res?.error === "Category already exists") {
          setErrorWithTimeout("Category already exists");
        } else {
          setErrorWithTimeout(res?.error || "An error occurred.");
        }
        setIsSubmitting(false);
        return;
      }

      setTimeout(() => {
        setIsSubmitting(false);
        handleEditModalClose();
        fetchCategories();
        showSuccess("Category updated successfully");
      }, 500);
    } catch (err) {
      setIsSubmitting(false);

      if (err.message === "REQUEST_TIMEOUT") {
        setErrorWithTimeout("Request timed out. Try again.");
      } else if (err.message === "NETWORK_ERROR") {
        setErrorWithTimeout("Network error. Check your connection.");
      } else {
        setErrorWithTimeout("Unexpected error occurred.");
      }
    }
  };

  const handleEditCategory = (category, allCategories = []) => {
    if (!allCategories || !Array.isArray(allCategories)) return;

    setEditCategoryId(category.id);
    setEditCategoryName(category.category_name);
    setOriginalCategoryName(category.category_name);


    setShowEditModal(true);

    const parentCategory = allCategories.find(
      (parent) =>
        parent.subcategories &&
        parent.subcategories.some((sub) => sub.id === category.id)
    );

    if (parentCategory) {
      setEditCategoryType("sub");
      setOriginalCategoryType("sub");
      setEditParentCategory(parentCategory.id);
      setOriginalParentCategory(parentCategory.id);
    } else {
      setEditCategoryType("main");
      setOriginalCategoryType("main");
      setEditParentCategory(null);
      setOriginalParentCategory(null);
    }
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const confirmDeleteCategory = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "delete-category",
        data: { id: categoryToDelete.id },
      });

      if (!res?.success) {
        setIsSubmitting(false);
        setErrorWithTimeout(res?.error || "Failed to delete category.");
        return;
      }

      setTimeout(() => {
        setIsSubmitting(false);
        setShowDeleteModal(false);
        fetchCategories();
        showSuccess("Category deleted successfully");
      }, 500);
    } catch (err) {
      setIsSubmitting(false);
      setErrorWithTimeout("An error occurred while deleting the category.");
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const setErrorWithTimeout = (errorMessage) => {
    setError(errorMessage);
    if (errorMessage) {
      setTimeout(() => {
        setError("");
      }, 3000);
    }
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const priority = {
      1: 1,
      17: 2,
      16: 99,
    };

    const aPriority = priority[a.id] || 50;
    const bPriority = priority[b.id] || 50;

    return aPriority - bPriority;
  });

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200";

  return (
    <main className="h-full overflow-y-auto">
      {showSuccessMessage && (
        <div className="fixed right-4 top-4 z-50 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 shadow-lg">
          {successMessage}
        </div>
      )}

      <div className="w-full">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4 md:px-6 md:py-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">
                  Categories
                </h1>
              </div>

              <button
                onClick={handleAddCategoryClick}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98]"
              >
                Add Category
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6">
            {categories.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
                No categories found.
              </div>
            ) : (
              <div className="space-y-4">
                {sortedCategories.map((category, index) => (
                  <div
                    key={category.id}
                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                  >
                    <div className="flex items-center justify-between gap-4 px-4 py-4 md:px-5">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700">
                          {index + 1}
                        </div>

                        <div className="min-w-0">
                          <p className="truncate text-base font-semibold capitalize text-slate-900">
                            {category.category_name}
                          </p>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                            Main category
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditCategory(category, categories)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                        >
                          <i className="fas fa-pencil-alt text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50"
                        >
                          <i className="fas fa-trash-alt text-sm"></i>
                        </button>
                      </div>
                    </div>

                    {category.subcategories?.length > 0 && (
                      <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 md:px-5">
                        <div className="space-y-3">
                          {category.subcategories.map((subCategory, subIndex) => (
                            <div
                              key={subCategory.id}
                              className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3"
                            >
                              <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-sm font-semibold text-amber-700">
                                  {String.fromCharCode(97 + subIndex)}
                                </div>

                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium capitalize text-slate-800 md:text-base">
                                    {subCategory.category_name}
                                  </p>
                                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                                    Sub category
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    handleEditCategory(subCategory, categories)
                                  }
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                                >
                                  <i className="fas fa-pencil-alt text-xs"></i>
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteCategory(subCategory)
                                  }
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50"
                                >
                                  <i className="fas fa-trash-alt text-xs"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAddCategoryModal && (
        <Modal
          title="Add Category"
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
          submitButtonText="Add Category"
          showCancel={false}
          className="modal-content"
          showSubmitButton={true}
        >
          <div className="space-y-5">
            <div>
              <input
                type="text"
                value={categoryName}
                placeholder="Enter category name"
                onChange={(e) => setCategoryName(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-800">
                Category Type
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 transition hover:bg-slate-50">
                  <input
                    type="radio"
                    value="main"
                    checked={categoryType === "main"}
                    onChange={() => {
                      setCategoryType("main");
                      setParentCategory(null);
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Main Category
                  </span>
                </label>

                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 transition hover:bg-slate-50">
                  <input
                    type="radio"
                    value="sub"
                    checked={categoryType === "sub"}
                    onChange={() => setCategoryType("sub")}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Sub Category
                  </span>
                </label>
              </div>
            </div>

            {categoryType === "sub" && (
              <div>
                <select
                  value={parentCategory || ""}
                  onChange={(e) => setParentCategory(e.target.value)}
                  className={inputClass}
                  required
                >
                  <option value="">Select Parent Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showEditModal && (
        <Modal
          title="Edit Category"
          onClose={handleEditModalClose}
          onSubmit={handleUpdateCategory}
          submitButtonText="Update"
          showCancel={false}
          showSubmitButton={true}
          className="modal-content"
        >
          <div className="space-y-5">
            <div>
              <input
                type="text"
                placeholder="Category Name"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-800">
                Category Type
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 transition hover:bg-slate-50">
                  <input
                    type="radio"
                    value="main"
                    checked={editCategoryType === "main"}
                    onChange={() => {
                      setEditCategoryType("main");
                      setEditParentCategory(null);
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Main Category
                  </span>
                </label>

                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 transition hover:bg-slate-50">
                  <input
                    type="radio"
                    value="sub"
                    checked={editCategoryType === "sub"}
                    onChange={() => setEditCategoryType("sub")}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Sub Category
                  </span>
                </label>
              </div>
            </div>

            {editCategoryType === "sub" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Select Parent Category
                </label>
                <select
                  value={editParentCategory || ""}
                  onChange={(e) => setEditParentCategory(e.target.value)}
                  className={inputClass}
                >
                  <option value="">-- Select Parent Category --</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          title="Delete Category"
          onClose={handleCloseModal}
          onSubmit={confirmDeleteCategory}
          submitButtonText="Delete"
          showCancel={true}
          showSubmitButton={true}
          className="modal-content delete-modal-content"
        >
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
            <p className="text-sm font-medium text-rose-700">
              Are you sure you want to delete this category?
            </p>
          </div>
        </Modal>
      )}

      {error && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl border border-rose-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Error</p>
                <p className="mt-1 text-sm text-slate-600">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900"></div>
        </div>
      )}
    </main>
  );
};

export default CategoriesPage;